import React, { useEffect, useState } from 'react';
import axios from 'axios';
import AdminSideMenu from '../components/UI/AdminSideMenu/AdminSideMenu';
import styles from './Analytics.module.css';
import { Box, Grid, Card, CardContent, Typography, TextField, Select, MenuItem, Button, CircularProgress } from '@mui/material';

const TestimonialsDashboard = () => {
  const token = sessionStorage?.getItem('afd8TvhsdjwiuuvsgjhsAfgsUhjs');
  const [q, setQ] = useState('');
  const [minRating, setMinRating] = useState('');
  const [maxRating, setMaxRating] = useState('');
  const [withComments, setWithComments] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(200);
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortDir, setSortDir] = useState('desc');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ kpis: {}, testimonials: [], total: 0 });

  // Persist query to URL
  useEffect(() => {
    const params = new URLSearchParams();
    if (q) params.set('q', q);
    if (minRating !== '') params.set('minRating', String(minRating));
    if (maxRating !== '') params.set('maxRating', String(maxRating));
    if (withComments) params.set('withComments', String(withComments));
    if (startDate) params.set('startDate', startDate);
    if (endDate) params.set('endDate', endDate);
    if (sortBy) params.set('sortBy', sortBy);
    if (sortDir) params.set('sortDir', sortDir);
    params.set('page', String(page));
    params.set('limit', String(limit));
    const url = `${window.location.pathname}?${params.toString()}`;
    window.history.replaceState({}, '', url);
  }, [q, minRating, maxRating, withComments, startDate, endDate, sortBy, sortDir, page, limit]);

  // Load from URL on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    setQ(urlParams.get('q') || '');
    setMinRating(urlParams.get('minRating') || '');
    setMaxRating(urlParams.get('maxRating') || '');
    setWithComments(urlParams.get('withComments') || '');
    setStartDate(urlParams.get('startDate') || '');
    setEndDate(urlParams.get('endDate') || '');
    setSortBy(urlParams.get('sortBy') || 'createdAt');
    setSortDir(urlParams.get('sortDir') || 'desc');
    setPage(parseInt(urlParams.get('page') || '1', 10));
    setLimit(parseInt(urlParams.get('limit') || '200', 10));
  }, []);

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        setLoading(true);
        const params = new URLSearchParams();
        if (q) params.set('q', q);
        if (minRating !== '') params.set('minRating', String(minRating));
        if (maxRating !== '') params.set('maxRating', String(maxRating));
        if (withComments) params.set('withComments', String(withComments));
        if (startDate) params.set('startDate', startDate);
        if (endDate) params.set('endDate', endDate);
        if (sortBy) params.set('sortBy', sortBy);
        if (sortDir) params.set('sortDir', sortDir);
        params.set('page', String(page));
        params.set('limit', String(limit));
        const resp = await axios.get(`/origin/testimonials?${params.toString()}`, { headers: { 'x-access-token': token } });
        setData(resp.data || { kpis: {}, testimonials: [], total: 0 });
      } catch (e) {
        // no-op
      } finally {
        setLoading(false);
      }
    };
    fetchTestimonials();
  }, [q, minRating, maxRating, withComments, startDate, endDate, sortBy, sortDir, page, limit, token]);

  const k = data.kpis || {};
  const rows = data.testimonials || [];
  const total = data.total || 0;

  return (
    <div className={styles.analyticsContainer}>
      <AdminSideMenu />
      <div className={styles.analyticsContent}>
        <Box className={styles.analyticsHeader}>
          <Box className={styles.headerLeft}>
            <Typography variant="h4" className={styles.pageTitle}>Testimonials</Typography>
            <Typography variant="subtitle1" className={styles.pageSubtitle}>User ratings and feedback collected across the app</Typography>
          </Box>
        </Box>

        {loading ? (
          <Box className={styles.loadingContainer}>
            <CircularProgress />
            <Typography variant="body1" sx={{ mt: 2 }}>
              Loading testimonials...
            </Typography>
          </Box>
        ) : (
          <>
            {/* KPIs */}
            <Grid container spacing={2} className={styles.kpiContainer}>
              <Grid item xs={12} sm={6} md={3}>
                <Card className={styles.kpiCard} sx={{ background: '#ffffff' }}>
                  <CardContent>
                    <Typography className={styles.kpiLabel}>Total Testimonials</Typography>
                    <Typography className={styles.kpiValue}>{k.total || 0}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card className={styles.kpiCard} sx={{ background: '#ffffff' }}>
                  <CardContent>
                    <Typography className={styles.kpiLabel}>Average Rating</Typography>
                    <Typography className={styles.kpiValue}>{(k.avgRating || 0).toFixed ? (k.avgRating || 0).toFixed(2) : Number(k.avgRating || 0).toFixed(2)}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Filters */}
            <Box display="flex" flexWrap="wrap" gap={2} alignItems="center" sx={{ background: '#fff', p: 2, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
              <TextField size="small" placeholder="Search name/comment" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} />
              <Select size="small" value={withComments} onChange={(e) => { setWithComments(e.target.value); setPage(1); }} displayEmpty>
                <MenuItem value=""><em>Any Comment Status</em></MenuItem>
                <MenuItem value="true">With Comments</MenuItem>
              </Select>
              <TextField size="small" placeholder="Min rating" type="number" inputProps={{ min: 0, max: 5 }} value={minRating} onChange={(e) => { setMinRating(e.target.value); setPage(1); }} />
              <TextField size="small" placeholder="Max rating" type="number" inputProps={{ min: 0, max: 5 }} value={maxRating} onChange={(e) => { setMaxRating(e.target.value); setPage(1); }} />
              <TextField size="small" type="date" value={startDate} onChange={(e) => { setStartDate(e.target.value); setPage(1); }} />
              <TextField size="small" type="date" value={endDate} onChange={(e) => { setEndDate(e.target.value); setPage(1); }} />
              <Select size="small" value={sortBy} onChange={(e) => { setSortBy(e.target.value); setPage(1); }}>
                <MenuItem value="createdAt">Sort by Date</MenuItem>
                <MenuItem value="rating">Sort by Rating</MenuItem>
                <MenuItem value="name">Sort by Name</MenuItem>
              </Select>
              <Select size="small" value={sortDir} onChange={(e) => { setSortDir(e.target.value); setPage(1); }}>
                <MenuItem value="desc">Desc</MenuItem>
                <MenuItem value="asc">Asc</MenuItem>
              </Select>
              <Button variant="outlined" onClick={() => { setQ(''); setMinRating(''); setMaxRating(''); setWithComments(''); setStartDate(''); setEndDate(''); setSortBy('createdAt'); setSortDir('desc'); setPage(1); }}>Reset</Button>
            </Box>

            {/* Testimonials table */}
            <Box className={styles.chartContainer} sx={{ p: 0 }}>
              <div style={{ padding: 16 }}>
                <Typography variant="h6">Testimonials</Typography>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: '#c0d1d457' }}>
                        <th style={{ textAlign: 'left', padding: 8 }}>Name</th>
                        <th style={{ textAlign: 'left', padding: 8 }}>Rating</th>
                        <th style={{ textAlign: 'left', padding: 8 }}>Comment</th>
                        <th style={{ textAlign: 'left', padding: 8 }}>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r, idx) => (
                        <tr key={idx} style={{ borderBottom: '1px solid #eee' }}>
                          <td style={{ padding: 8 }}>{r.name}</td>
                          <td style={{ padding: 8 }}>{typeof r.rating === 'number' ? r.rating.toFixed(1) : (r.rating || '-')}</td>
                          <td style={{ padding: 8, maxWidth: 500, whiteSpace: 'pre-wrap' }}>{r.comment || '-'}</td>
                          <td style={{ padding: 8 }}>{r.createdAt}</td>
                        </tr>
                      ))}
                      {rows.length === 0 && (
                        <tr><td colSpan={4} style={{ padding: 8, color: '#666' }}>No testimonials</td></tr>
                      )}
                    </tbody>
                  </table>
                  <Box display="flex" justifyContent="flex-end" gap={2} mt={2}>
                    <Typography variant="body2">Page {page}</Typography>
                    <Button size="small" variant="outlined" disabled={page <= 1 || loading} onClick={() => setPage((p) => Math.max(1, p - 1))}>Prev</Button>
                    <Button size="small" variant="outlined" disabled={(page * limit) >= total || loading} onClick={() => setPage((p) => p + 1)}>Next</Button>
                    <Select size="small" value={String(limit)} onChange={(e) => { setLimit(parseInt(e.target.value, 10)); setPage(1); }}>
                      {[50, 100, 200, 500].map(n => <MenuItem key={n} value={String(n)}>{n}/page</MenuItem>) }
                    </Select>
                  </Box>
                </div>
              </div>
            </Box>
          </>
        )}
      </div>
    </div>
  );
};

export default TestimonialsDashboard;
